"use client";

import { useTranslations } from "next-intl";
import { Link } from "@/i18n/routing";
import { buttonClasses } from "@/components/ui/Button";
import { capturePostHogEvent } from "@/lib/posthog";
import type { Vehicle } from "@/lib/pdpCopy";
import { useCardsOpen } from "@/lib/useCardsOpen";

// Every landing CTA click, tagged with where on the page it was and which angle the visitor
// arrived on. Outlived the copy experiment it was built for — it is the funnel's first step.
export const CTA_CLICK_EVENT = "landing_cta_clicked";

type LandingCtaProps = {
  /** Button copy. */
  label: string;
  /** Where on the lander this CTA sits — recorded on the click event. */
  location: string;
  /** Active homepage angle — recorded on the click event. */
  angle: string;
  /** Extra classes merged into the button (e.g. full-width in the PDP buy box). */
  className?: string;
  /**
   * Which lander the click came from. For cars and motorcycles it is recorded only — the funnel asks
   * which one. The pets lander skips that question, so it also decides where the click goes. The
   * cards lander goes straight to the card builder (/card-family): people, pets, the car, the house.
   */
  vehicle?: Vehicle;
};

export function LandingCta({ label, location, angle, className, vehicle = "car" }: LandingCtaProps) {
  if (vehicle === "card") return <CardsCta angle={angle} className={className} label={label} location={location} />;
  return <CtaLink angle={angle} className={className} label={label} location={location} vehicle={vehicle} />;
}

/** The cards lander's button: after the Christmas deadline it says ordering has closed and leads nowhere. */
function CardsCta(props: Omit<LandingCtaProps, "vehicle">) {
  const open = useCardsOpen();
  // `cart`: the lander is outside the funnel, and only the shell namespaces reach its browser.
  const t = useTranslations("cart");
  if (open) return <CtaLink {...props} vehicle="card" />;
  return (
    <span aria-disabled="true" className={buttonClasses("primary", `pointer-events-none opacity-60 ${props.className ?? ""}`)}>
      {t("cardsClosed")}
    </span>
  );
}

function CtaLink({ label, location, angle, className, vehicle }: LandingCtaProps & { vehicle: Vehicle }) {
  return (
    <Link
      className={buttonClasses("primary", className)}
      href={vehicle === "pet" ? "/pet-upload" : vehicle === "card" ? "/card-family" : "/upload"}
      onClick={() =>
        capturePostHogEvent(CTA_CLICK_EVENT, {
          cta_location: location,
          cta_label: label,
          angle,
          vehicle,
        })
      }
    >
      {label}
    </Link>
  );
}
